/**
 * Wire protocol for session-level actions (dice rolls, log broadcasts).
 *
 * TypeScript mirror of src/lambda/shared/GoobDoobVtt.Domain/SessionMessages.cs.
 * Both implementations must stay in lockstep — when one changes, the other has
 * to change too.
 *
 * Session actions are distinct from runtime actions: they're not tied to a
 * single character's state and don't carry version numbers. Dice rolls are the
 * first action in this namespace; future candidates include initiative rolls,
 * rest broadcasts, and DM announcements.
 */

export const SessionActions = {
  Roll: 'session/roll',
} as const;

export const SessionServerTypes = {
  LogEntry: 'session/log-entry',
} as const;

// ----------------- inbound -----------------

/**
 * Client-resolved dice roll. The client computes the roll via
 * crypto.getRandomValues and ships the results to the server for audit and
 * broadcast. The server never re-rolls.
 */
export interface RollRequest {
  action: typeof SessionActions.Roll;
  /** Standard dice notation, e.g. "2d6+3", "1d20". */
  expression: string;
  /** Individual die results in roll order. */
  results: number[];
  /** Computed total including modifier. */
  total: number;
  /** Optional label — "attack roll", "fire bolt damage", etc. */
  reason?: string;
}

// ----------------- outbound -----------------

/**
 * Broadcast to all session connections for the session log panel. Both dice
 * rolls and runtime mutations produce these; the `kind` field discriminates
 * so the UI can pick a rendering style.
 */
export interface LogEntryPayload {
  id: string;
  ts: string;
  actorUsername: string;
  /** "roll" or "mutation" — drives icon and formatting in the log panel. */
  kind: 'roll' | 'mutation';
  /** Human-readable summary line. */
  summary: string;
  /** Dice expression for roll entries; action string for mutations. */
  expression?: string;
  /** Individual die results for roll entries. */
  results?: number[];
  /** Computed total for roll entries. */
  total?: number;
  /** Optional reason/label. */
  reason?: string;
}
